import React from 'react';
import clsx from 'clsx';
import { useDrop } from 'react-dnd';
import { Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { grey, lightBlue } from '@mui/material/colors';

import { BoardCard } from './BoardCard';
import { BoardColumnProps } from './types';
import { DND_CARD_TYPE } from './constants';

export function BoardColumn<T>({
  id,
  title,
  cards,
  metaData,
  renderCard,
  onMoveCard,
  renderColumnHeader,
}: BoardColumnProps<T>) {
  const classes = useStyles();

  const [{ isOver, canDrop }, dropCard] = useDrop({
    accept: DND_CARD_TYPE,
    collect: monitor => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
    drop: (dragItem: any) => {
      if (dragItem.columnId !== id && metaData.isSupportDAD) {
        onMoveCard({
          toColumnId: id,
          toIndex: cards.length,
          itemIndex: dragItem.index,
          fromColumnId: dragItem.columnId,
        });
      }
    },
  });

  return (
    <Box className={classes.boardColumn}>
      <Box className="headerSpace">
        {renderColumnHeader ? (
          renderColumnHeader({ id, title, cards, metaData })
        ) : (
          <Typography sx={{ color: metaData.nameColor }} variant="h6">
            {title}
          </Typography>
        )}
      </Box>

      <Box ref={dropCard} className={clsx('cardsSpace', { cardsOverSpace: isOver && canDrop })}>
        {isOver && canDrop ? (
          <Box className={classes.overColumn}>
            <Typography variant="subtitle1" component="span">
              {title}
            </Typography>
          </Box>
        ) : (
          cards.map((card, index) => (
            <BoardCard<T>
              key={id}
              id={id}
              card={card}
              index={index}
              columnId={id}
              metaData={metaData}
              onMoveCard={onMoveCard}
            >
              {renderCard(card, id)}
            </BoardCard>
          ))
        )}
      </Box>
    </Box>
  );
}

const useStyles = makeStyles(({ palette }) => ({
  overColumn: {
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  boardColumn: {
    width: '100%',
    gap: '0.25rem',
    height: '100%',
    display: 'flex',
    minWidth: '20rem',
    flexDirection: 'column',

    '& .headerSpace': {
      borderRadius: '0.3rem',
      padding: '0.75rem 1rem',
      border: `1px solid ${grey[300]}`,
      backgroundColor: palette.background.paper,
    },

    '& .cardsSpace': {
      gap: '0.25rem',
      height: '100%',
      display: 'flex',
      overflow: 'auto',
      borderRadius: '0.3rem',
      flexDirection: 'column',
      padding: '0.5rem 0.75rem',
      border: `1px solid ${grey[300]}`,
      backgroundColor: palette.background.paper,

      scrollbarWidth: 'none',
      msOverflowStyle: 'none',

      '&::-webkit-scrollbar': {
        display: 'none',
      },
    },

    '& .cardsOverSpace': {
      backgroundColor: lightBlue[50],
    },
  },
}));
